import { useState, useRef } from 'react'


import { zodResolver } from '@hookform/resolvers/zod'
import _ from 'lodash'
import ReCAPTCHA from 'react-google-recaptcha'
import { FormProvider, useForm, type Resolver } from 'react-hook-form'
import { toast } from 'sonner'



import { env } from '@/env'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { Button } from '@/shared/ui'
import { AppIcon  } from '@/shared/ui/icons'
import { Overlay } from '@/shared/ui/Overlay'

import { useAuthContext } from '../model/auth-context'
import { getSignUpSchema, type SignUpValues } from '../model/schemas'
import { SignUpFields } from './fields/SignUpFields'
import { AuthProviderRows } from './providers/AuthProviderRows'
import { getSocialProviders } from './providers/SocialProviders'

const isDev = env.NODE_ENV === 'development'

const generateFieldValues = () => {
  const email = `wendell+${(Math.random() + 1)
    .toString(36)
    .substring(7)}@tootz.com.br`

  return {
    name: 'Wendell Adriel',
    email,
    email_confirmation: email,
    password: '123123123',
    password_confirmation: '123123123'
  }
}

export const SignUpForm = ({
  beforeConfirmation,
  onSignUp,
  onError,
  withTerms,
  additionalValidationSchema,
  children
}) => {
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const {
    providers,
    providersLoaded,
    httpClient,
    styleConfig,
    goToSignIn,
    goToConfirmation,
    handleSignIn,
    setCurrentEmail,
    callbackUrl,
    reCaptchaEnabled,
    useReCaptchaV3,
    reCaptchaKey,
    config
  } = useAuthContext()
  const withEmailConfirmation = _.get(
    config,
    'signUp.withEmailConfirmation',
    false
  )
  const header =
    _.has(config, 'signUp.header') && _.get(config, 'signUp.header', {})
  const skipConfirmation = _.get(config, 'signUp.skipConfirmation', false)
  const hideSignInAction = _.get(config, 'signUp.hideSignInAction', false)
  const hasSocialProviders =
    Object.keys(getSocialProviders(providers)).length > 0
  const validationSchema = getSignUpSchema({
    withTerms: !!withTerms,
    withEmailConfirmation
  })
  // O schema normaliza `null` antes de validar (o Yup aceitava por
  // `.nullable()`), o que torna o tipo de entrada `unknown`. Declarar a saída
  // aqui mantém `handleSubmit` e `setError` tipados nos campos reais.
  const methods = useForm<SignUpValues>({
    defaultValues: isDev ? generateFieldValues() : {},
    resolver: zodResolver(validationSchema) as Resolver<SignUpValues>
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [escolheuEmail, setEscolheuEmail] = useState(false)

  const { t } = useTranslation()

  const handleSignUp = async data => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      if (reCaptchaEnabled) {
        let recaptchaValue

        if (useReCaptchaV3)
          recaptchaValue = await reCaptchaRef.current.execute()
        else recaptchaValue = reCaptchaRef.current.getValue()

        if (!recaptchaValue) {
          toast.error('Antes de entrar, use o reCAPTCHA pra verificar sua identidade', {
            description: 'Oops...'
          })

          setIsSubmitting(false)
          return
        }
      }

      const { name, email, password, password_confirmation } = data

      setCurrentEmail(email)

      await httpClient.post('/auth/sign-up', {
        ...data,
        name,
        email,
        password,
        password_confirmation
      })

      if (skipConfirmation) {
        if (typeof onSignUp !== 'undefined' && _.isFunction(onSignUp))
          onSignUp({ email, password })
        else {
          const { ok, error } = await handleSignIn({
            login: email,
            password
          })

          if (ok) return window.location.replace(callbackUrl)
          if (error) goToSignIn()
        }
      } else {
        _.isFunction(beforeConfirmation) &&
          (await beforeConfirmation({ email, password, ...data }))
        goToConfirmation({ email, password })
      }
    } catch (error) {
      if (typeof onError !== 'undefined' && _.isFunction(onError))
        onError({ error })
      else {
        methods.setError('email', {
          type: 'custom',
          message: t(
            'emailIsAlreadyInUse',
            {},
            { fallback: 'Email já está em uso' }
          )
        })
      }
    }

    setIsSubmitting(false)
  }

  // Sem provedor social, o formulário é a única opção: aparece direto, e não
  // há para onde voltar. Mas só depois que a busca termina — decidir antes
  // fazia a tela abrir o formulário e recolhê-lo no quadro seguinte.
  const emailAberto = escolheuEmail || (providersLoaded && !hasSocialProviders)
  const onBack =
    emailAberto && hasSocialProviders ? () => setEscolheuEmail(false) : undefined

  const emailFields = (
    <>
      <SignUpFields withTerms={withTerms}>{children}</SignUpFields>

      {reCaptchaEnabled && (
        <div
          className={`flex items-center justify-center w-full${useReCaptchaV3 ? '' : ' mb-6'}`}
        >
          <ReCAPTCHA
            ref={reCaptchaRef}
            sitekey={reCaptchaKey}
            size={useReCaptchaV3 ? 'invisible' : 'normal'}
          />
        </div>
      )}

      {!!withTerms && _.isObject(withTerms) && _.get(withTerms, 'autoAgree') && (
        <div className="mb-6">
          <label className="text-gray-700 text-base text-center">
            {`${t(
              'byProceedingYouAgreeToThe',
              {},
              { fallback: 'Ao prosseguir, você concorda com os' }
            )} `}
            <a
              href={withTerms.href}
              target="_blank"
              rel="noreferrer"
              className="font-bold"
            >
              {`${t('auth.termsOfUse', {}, { fallback: 'Termos de Uso' })} `}
              <span className="font-normal">
                {' '}
                ({t('auth.clickToRead', {}, { fallback: 'clique para ler' })})
              </span>
            </a>{' '}
            {t('auth.ofThePlatform', {}, { fallback: 'da plataforma' })}
          </label>
        </div>
      )}

      {/* Voltar e enviar dividem a linha — ver a mesma decisão em SignInForm. */}
      <div className="mt-8 flex items-center gap-2">
      {onBack ? (
        <Button
          type="button"
          variant="light"
          className="shrink-0"
          onClick={onBack}
          showTrailingIcon={false}
          leadingIcon={<AppIcon name="arrow-left" size={16} />}
          aria-label="Voltar para as opções de cadastro"
        >
          Voltar
        </Button>
      ) : null}
      <Button
        type="submit"
        variant="primary"
        className="grow"
        loading={isSubmitting}
        showTrailingIcon={false}
      >
        {isSubmitting
          ? t('auth.creatingAccount', {}, { fallback: 'Criando conta...' })
          : t('auth.createAccount', {}, { fallback: 'Criar conta' })}
      </Button>
      </div>
    </>
  )

  return (
    <Overlay
      block
      show={false}
      active={isSubmitting}
      opacity="50"
      layer={
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" role="status">
          <span className="sr-only">
            {t('auth.loading', {}, { fallback: 'Carregando...' })}
          </span>
        </div>
      }
    >

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSignUp)}>
          {/* Alternância, não acordeão: com os campos abertos abaixo das
              linhas de provedor, o "Voltar" não tinha para onde voltar — nada
              havia sido escondido. Agora a tela mostra as opções OU o
              formulário, como o acesso já fazia. */}
          <div className="flex flex-col gap-3">
            {!emailAberto ? (
              <AuthProviderRows
                verb="Cadastrar"
                onEmailSelect={() => setEscolheuEmail(true)}
              />
            ) : (
              <div>{emailFields}</div>
            )}
          </div>

          {/* O convite para entrar vive no rodapé da página (`AuthPageFooter`),
              não aqui: com os dois, a tela mostrava "Já possui conta? Entrar"
              logo acima de "Já tem uma conta? Faça login" — duas vezes a mesma
              oferta, com textos diferentes. O `SignInForm` já seguia essa regra
              via `layoutVariant`; este formulário não. */}
        </form>
      </FormProvider>
    </Overlay>
  )
}
